import { useQuery } from "@apollo/client";
import OverdueSubscriptionStickyBanner from "components/billing/OverdueSubscriptionStickyBanner";
import { CompanyContainer } from "components/company/v3/CompanyContainers";
import AmericanIssuerSeo from "components/company/v3/seo/AmericanIssuerSeo";
import AmericanCompanyContext from "components/company/v3/context/AmericanCompanyContext";
import { CompanyContextEnum } from "components/company/v3/menu/enums";
import getMatchedSecurity from "components/company/v3/utils/getMatchedSecurity";
import LocaleContext from "components/i18n/LocaleContext";
import NavbarLayout from "components/layouts/NavbarLayout";
import CompanyNavbarLayout from "components/company/v3/layouts/CompanyNavbarLayout";
import LoadingSkeleton from "components/pages/TemplatesGalleryPage/LoadingSkeleton";
import SetHttpStatusCode from "lib/http/SetHttpStatusCode";
import IssuerByTickerSymbolQuery from "lib/queries/IssuerByTickerSymbolQuery";
import type {
  IIssuerByTickerSymbolQuery,
  IIssuerByTickerSymbolQueryVariables,
} from "lib/queries/__generated__/IssuerByTickerSymbolQuery.generated";
import NotFound from "pages/404";
import { ISecurityCountry } from "types/graphqlTypes";

interface AmericanPageLayoutProps {
  tickerSymbol: string;
  children: React.ReactNode;
}

export default function AmericanPageLayout({ tickerSymbol, children }: AmericanPageLayoutProps) {
  const {
    data: currentData,
    loading,
    previousData,
  } = useQuery<IIssuerByTickerSymbolQuery, IIssuerByTickerSymbolQueryVariables>(IssuerByTickerSymbolQuery, {
    variables: {
      tickerSymbol,
      country: ISecurityCountry.Us,
    },
  });

  const data = currentData || previousData;
  if (loading) {
    return (
      <NavbarLayout>
        <OverdueSubscriptionStickyBanner />
        <LocaleContext.Provider value="USA">
          <div className="flex flex-col gap-4 pb-6">
            <LoadingSkeleton count={1} className="h-16 w-full rounded-none" />

            <CompanyContainer>
              <LoadingSkeleton count={1} className="h-96 xl:h-company-box" />
            </CompanyContainer>
          </div>
        </LocaleContext.Provider>
      </NavbarLayout>
    );
  }

  if (!data || !data.issuerByTickerSymbol || data.issuerByTickerSymbol.__typename !== "AmericanCompanyIssuer") {
    return (
      <>
        <SetHttpStatusCode httpStatus={404} />
        <NotFound />
      </>
    );
  }

  const security = getMatchedSecurity(data.issuerByTickerSymbol, tickerSymbol);

  if (!security || security.__typename !== "AmericanCompanyIssuerSecurity") {
    return (
      <>
        <SetHttpStatusCode httpStatus={404} />
        <NotFound />
      </>
    );
  }

  return (
    <NavbarLayout>
      <OverdueSubscriptionStickyBanner />
      <LocaleContext.Provider value="USA">
        <AmericanCompanyContext.Provider value={data.issuerByTickerSymbol}>
          <CompanyNavbarLayout context={CompanyContextEnum.AmericanCompany} tickerSymbol={tickerSymbol}>
            <AmericanIssuerSeo />
            {children}
          </CompanyNavbarLayout>
        </AmericanCompanyContext.Provider>
      </LocaleContext.Provider>
    </NavbarLayout>
  );
}
